import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Loader2, Lock, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Acceso al panel PetMoon" },
      {
        name: "description",
        content:
          "Acceso privado para el equipo PetMoon: administra las solicitudes de viaje de mascotas en Guadalajara y Zapopan.",
      },
      { property: "og:title", content: "Acceso al panel PetMoon" },
      {
        property: "og:description",
        content: "Área privada del equipo PetMoon para gestionar viajes.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [modo, setModo] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMsg(null);
    if (modo === "login") {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      setLoading(false);
      if (error) return setMsg("No pudimos iniciar sesión. Revisa tu correo y contraseña.");
      void navigate({ to: "/panel" });
    } else {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}/panel` },
      });
      setLoading(false);
      if (error) return setMsg(error.message);
      setMsg("Cuenta creada. Revisa tu correo para confirmarla y luego inicia sesión.");
      setModo("login");
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-secondary/40 px-5 py-16 font-sans">
      <div className="w-full max-w-md rounded-[2rem] bg-card p-8 shadow-card ring-1 ring-border">
        <h1 className="font-display text-2xl font-bold text-primary">
          Panel PetMoon 🌙
        </h1>
        <p className="mt-2 text-sm font-semibold text-muted-foreground">
          Acceso exclusivo del equipo para aceptar o rechazar viajes.
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="field-label">
              <Mail className="mr-1.5 inline h-4 w-4 text-gold-deep" aria-hidden />
              Correo
            </span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-2xl border-[1.5px] border-input bg-background px-4 py-3.5 text-sm font-semibold outline-none focus:border-accent"
              placeholder="tucorreo@ejemplo.com"
            />
          </label>
          <label className="block">
            <span className="field-label">
              <Lock className="mr-1.5 inline h-4 w-4 text-gold-deep" aria-hidden />
              Contraseña
            </span>
            <input
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-2xl border-[1.5px] border-input bg-background px-4 py-3.5 text-sm font-semibold outline-none focus:border-accent"
              placeholder="••••••••"
            />
          </label>

          {msg && (
            <p className="rounded-2xl bg-secondary px-4 py-3 text-sm font-bold text-foreground">
              {msg}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-sm font-extrabold text-primary-foreground shadow-soft transition-all hover:-translate-y-0.5 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
            {modo === "login" ? "Entrar al panel" : "Crear cuenta"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => setModo((m) => (m === "login" ? "signup" : "login"))}
          className="mt-5 w-full text-center text-sm font-extrabold text-muted-foreground transition-colors hover:text-primary"
        >
          {modo === "login" ? "Crear una cuenta nueva" : "Ya tengo cuenta, iniciar sesión"}
        </button>

        <Link
          to="/"
          className="mt-4 block text-center text-xs font-bold text-muted-foreground hover:text-primary"
        >
          Volver al inicio
        </Link>
      </div>
    </main>
  );
}
